// react_frontend/src/Components/Modules/Category/AddCategory.js
import React, { useState, useEffect, useRef, useContext } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { MotherContext } from "../../../Context/MotherContext";

// RSuite Components
import {
  Form,
  Button,
  Message,
  Loader,
  Whisper,
  Tooltip,
  Schema,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaTags,
  FaLink,
  FaQrcode,
  FaClipboardList,
  FaPlus,
} from "react-icons/fa";

const AddCategory = () => {
  const navigate = useNavigate();
  const { addCategoryToList } = useContext(MotherContext);
  const [formValue, setFormValue] = useState({
    category_name: "",
    category_slug: "",
    category_code: "",
  });
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const [categoryCount, setCategoryCount] = useState(0);
  const formRef = useRef();

  // Fetch category count on mount
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await AxiosAuthInstance.post(`count_total_category`);
        const count = response.data.total_catagory || 0;
        setCategoryCount(count);
        // Set default code: 001, 002, etc.
        setFormValue((prev) => ({
          ...prev,
          category_code: String(count + 1).padStart(3, "0"),
        }));
      } catch (error) {
        console.error("Failed to fetch category count:", error);
        // Fallback: start from 001
        setFormValue((prev) => ({
          ...prev,
          category_code: "001",
        }));
      }
    };

    fetchCount();
  }, []);

  // Auto-generate slug when name changes
  const handleNameChange = (value) => {
    const slug = value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    setFormValue({
      ...formValue,
      category_name: value,
      category_slug: slug,
    });
  };

  // Validation Schema
  const { StringType } = Schema.Types;

  const model = Schema.Model({
    category_name: StringType().isRequired("Category name is required."),
    category_slug: StringType().isRequired("Slug is auto-generated but required."),
    category_code: StringType().isRequired("Category code is required."),
  });

  // Handle Submit
  const handleSubmit = async () => {
    if (!formRef.current.check()) return;

    setLoading(true);
    try {
      const res = await AxiosAuthInstance.post("add_category", formValue);

      if (res.data.status === true) {
        toaster.push(
          <Message type="success" duration={3000}>
            Category added successfully!
          </Message>,
          { placement: "topEnd" }
        );
        addCategoryToList(res.data.category);
        navigate("/category");
      }
    } catch (err) {
      const serverErrors = err.response?.data?.errors || {};
      const formattedErrors = Object.keys(serverErrors).reduce((acc, key) => {
        acc[key] = serverErrors[key][0];
        return acc;
      }, {});
      setFormError(formattedErrors);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/category");
  };

  return (
    <>
      <Breadcrumb title="Add Category" />

      <div className="p-3">
        <div
          className="card shadow-sm border-0 mx-auto"
          style={{ maxWidth: "700px" }}
        >
          {/* Header */}
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaPlus /> Add New Category
            </h5>
            <Link to="/category" className="text-white text-decoration-none">
              <Whisper placement="left" speaker={<Tooltip>Category List</Tooltip>}>
                <Button size="sm" appearance="subtle" className="text-white">
                  <FaClipboardList /> List
                </Button>
              </Whisper>
            </Link>
          </div>

          {/* Body */}
          <div className="card-body bg-light p-4">
            <Form
              ref={formRef}
              fluid
              formValue={formValue}
              formError={formError}
              onChange={setFormValue}
              onCheck={setFormError}
              model={model}
            >
              {/* Category Name */}
              <Form.Group controlId="category_name">
                <Form.ControlLabel>Category Name *</Form.ControlLabel>
                <Form.Control
                  name="category_name"
                  placeholder="e.g., Electronics"
                  prefix={<FaTags />}
                  onChange={handleNameChange}
                />
                <Form.HelpText className="text-muted">
                  Slug will be auto-generated from name.
                </Form.HelpText>
                <Form.ErrorMessage name="category_name" />
              </Form.Group>

              {/* Category Slug */}
              <Form.Group controlId="category_slug">
                <Form.ControlLabel>Category Slug *</Form.ControlLabel>
                <Form.Control
                  name="category_slug"
                  placeholder="electronics"
                  prefix={<FaLink />}
                  readOnly
                  style={{ backgroundColor: "#f8f9fa", fontStyle: "italic" }}
                />
                <Form.HelpText className="text-muted">
                  Auto-generated. Edit name to change slug.
                </Form.HelpText>
                <Form.ErrorMessage name="category_slug" />
              </Form.Group>

              {/* Category Code */}
              <Form.Group controlId="category_code">
                <Form.ControlLabel>Category Code *</Form.ControlLabel>
                <Form.Control
                  name="category_code"
                  value={formValue.category_code}
                  readOnly
                  style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}
                  prefix={<FaQrcode />}
                />
                <Form.HelpText className="text-muted">
                  Auto-generated.
                </Form.HelpText>
                <Form.ErrorMessage name="category_code" />
              </Form.Group>

              {/* Action Buttons */}
              <div className="d-flex gap-3 pt-3 border-top" style={{ marginTop: "2rem" }}>
                <Button
                  appearance="primary"
                  color="blue"
                  size="md"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? (
                    <>
                      <Loader size="sm" /> Adding...
                    </>
                  ) : (
                    "Add Category"
                  )}
                </Button>

                <Button
                  className="border"
                  appearance="subtle"
                  color="red"
                  size="md"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCategory;